import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faPlaneDeparture, faPlaneArrival, faCar, faHotel, faMapMarkerAlt, faTasks, faStickyNote, 
    faSave, faArrowRight, faCalendarDay, faClock, faBed, faMountainSun, faRoute, faStar, faPlane, faPen,
    faChevronDown, faChevronUp, faCheck, faTimes // Added faCheck and faTimes
} from '@fortawesome/free-solid-svg-icons';
import './PackageItinerary.css';
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";

// --- Reusable Modal Component ---
const SelectionModal = ({ isOpen, onClose, title, children }) => {
    if (!isOpen) return null;
    return (
        <div className="itinerary-modal-backdrop" onClick={onClose}>
            <div className="itinerary-modal-content" onClick={e => e.stopPropagation()}>
                <div className="itinerary-modal-header">
                    <h5>{title}</h5>
                    <button className="close-btn" onClick={onClose}>&times;</button>
                </div>
                <div className="itinerary-modal-body">{children}</div>
            </div>
        </div>
    );
};

const FlightDetailCard = ({ flight, onChangeClick }) => {
    // Check 1: Is there any flight data at all?
    if (!flight) return <div className="detail-card placeholder-card">No Flight Data Available</div>;

    // Check 2: Does it have the complex "dcDisplayLegs" from a real API?
    if (flight.dcDisplayLegs && flight.dcDisplayLegs.length > 0) {
        // --- THIS IS YOUR ORIGINAL CODE FOR REAL API DATA ---
        const leg = flight.dcDisplayLegs[0];
        const departureTime = moment(leg.startSegment.departureDateTime).format("HH:mm");
        const arrivalTime = moment(leg.endSegment.arrivalDateTime).format("HH:mm");
        const formatDuration = (min) => `${Math.floor(min / 60)}h ${min % 60}m`;
        const airlineLogo = `/assets/images/airlinelogos/${leg.airlineData.airlineCode}.png`;
        
        return (
            <div className="detail-card flight-detail-card">
                 <div className="detail-card-body">
                    <div className="flight-content">
                        <div className="airline-info">
                            <div className="airline-logo">
                                <img src={airlineLogo} alt={leg.airlineData.airline} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                <div className="airline-fallback">{leg.airlineData.airlineCode}</div>
                            </div>
                            <div className="airline-details">
                                <div className="airline-name">{leg.airlineData.airline}</div>
                                <div className="flight-number">{leg.airlineData.airlineCode} {leg.airlineData.flightNumber}</div>
                            </div>
                        </div>
                        <div className="flight-timeline-section">
                            <div className="flight-endpoint departure">
                                <div className="time">{departureTime}</div>
                                <div className="airport-code">{leg.startSegment.airportCode}</div>
                                <div className="city-name">{leg.startSegment.city}</div>
                            </div>
                            <div className="journey-info">
                                <div className="duration"><FontAwesomeIcon icon={faClock} /> {formatDuration(leg.totalDuration)}</div>
                                <div className="arrow-line"><div className="line"></div><FontAwesomeIcon icon={faPlane} className="plane-icon" /></div>
                                <div className="stops">{leg.stops === 0 ? 'Non-Stop' : `${leg.stops} Stop(s)`}</div>
                            </div>
                            <div className="flight-endpoint arrival">
                                <div className="time">{arrivalTime}</div>
                                <div className="airport-code">{leg.endSegment.airportCode}</div>
                                <div className="city-name">{leg.endSegment.city}</div>
                            </div>
                            {onChangeClick && (
                                <div className="change-button-container">
                                    <button className="change-btn" onClick={onChangeClick}><FontAwesomeIcon icon={faPen} /> Change</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );

    } else if (flight.dcFlightLegs && flight.dcFlightLegs.length > 0) {
        // --- THIS IS THE NEW FALLBACK FOR SIMPLE AI DATA ---
        const leg = flight.dcFlightLegs[0];
        const airlineCode = leg.supplierName?.substring(0, 2).toUpperCase() || 'AI';
        const airlineLogo = `/assets/images/airlinelogos/${airlineCode}.png`;

        return (
            <div className="detail-card flight-detail-card">
                <div className="detail-card-body">
                    <div className="flight-content">
                        <div className="airline-info">
                            <div className="airline-logo">
                                <img src={airlineLogo} alt={leg.supplierName} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
                                <div className="airline-fallback">{airlineCode}</div>
                            </div>
                            <div className="airline-details">
                                <div className="airline-name">{leg.supplierName}</div>
                                {leg.flightNumber && <div className="flight-number">{leg.flightNumber}</div>}
                            </div>
                        </div>
                        <div className="flight-timeline-section simple-view">
                            <div className="flight-endpoint departure">
                                <div className="airport-code">{flight.locationFrom}</div>
                            </div>
                            <div className="journey-info">
                                <div className="arrow-line"><div className="line"></div><FontAwesomeIcon icon={faPlane} className="plane-icon" /></div>
                            </div>
                            <div className="flight-endpoint arrival">
                                <div className="airport-code">{flight.locationTo}</div>
                            </div>
                            {onChangeClick && (
                                <div className="change-button-container">
                                    <button className="change-btn" onClick={onChangeClick}><FontAwesomeIcon icon={faPen} /> Change</button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return <div className="detail-card placeholder-card">Flight Data Incomplete</div>;
};

const FlightSelectionCard = ({ flight, onSelect, isSelected }) => {
    // Check for real API data first
    if (flight.dcDisplayLegs && flight.dcDisplayLegs.length > 0) {
        const leg = flight.dcDisplayLegs[0];
        // ... (your original detailed JSX for real data goes here)
        // This will work for Task 2 later
        return (
             <div className="selection-card flight-selection-card">
                {/* Your full, detailed JSX for real flights */}
             </div>
        );
    } 
    // Fallback for simple AI data
    else if (flight.dcFlightLegs && flight.dcFlightLegs.length > 0) {
        const leg = flight.dcFlightLegs[0];
        const airlineCode = leg.supplierName?.substring(0, 2).toUpperCase() || 'AI';
        const airlineLogo = `/assets/images/airlinelogos/${airlineCode}.png`;

        return (
            <div className="selection-card flight-selection-card">
                <div className="selection-card-body">
                    <div className="airline-info">
                        <div className="airline-logo">
                            <img src={airlineLogo} alt={leg.supplierName} onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
                            <div className="airline-fallback">{airlineCode}</div>
                        </div>
                        <div className="airline-details">
                            <div className="airline-name">{leg.supplierName}</div>
                            {leg.flightNumber && <div className="flight-number">{leg.flightNumber}</div>}
                        </div>
                    </div>
                    <div className="flight-timeline-section simple-view">
                        <div className="flight-endpoint departure"><div className="airport-code">{flight.locationFrom}</div></div>
                        <div className="journey-info"><div className="arrow-line"><div className="line"></div></div></div>
                        <div className="flight-endpoint arrival"><div className="airport-code">{flight.locationTo}</div></div>
                    </div>
                    <div className="select-button-container">
                {isSelected ? (
                    <button className="select-btn selected-btn" disabled>
                        Selected
                    </button>
                ) : (
                    <button className="select-btn" onClick={() => onSelect(flight)}>
                        Select Flight
                    </button>
                )}
            </div>
                </div>
            </div>
        );
    }
    return null; // Don't render if data is invalid
};
const TransferDetailCard = ({ transfer }) => {
  if (!transfer)
    return (
      <div className="detail-card placeholder-card">
        No Transfer Data Available
      </div>
    );

  return (
    <div className="detail-card transfer-detail-card">
      <div className="detail-card-body">
        {/* Icon */}
        <div className="transfer-icon">
          <FontAwesomeIcon icon={faCar} />
        </div>

        {/* Combined Info */}
        <div className="transfer-info">
          <span className="vehicle-type">{transfer.vehicleType}</span>
          <span className="location">{transfer.from}</span>
          <FontAwesomeIcon icon={faArrowRight} className="transfer-arrow" />
          <span className="location">{transfer.to}</span>
        </div>
      </div>
    </div>
  );
};



const HotelDetailCard = ({ hotel, onChangeClick }) => {
  if (!hotel) return null;

  return (
   <div className="detail-card hotel-detail-card">
  <div className="detail-card-body">
    {/* Left side - all info in one row */}
    <div className="hotel-info">
      <div className="hotel-name">
        <FontAwesomeIcon icon={faHotel} /> {hotel.hotelName}
      </div>
      <div className="hotel-location">
        <FontAwesomeIcon icon={faMapMarkerAlt} /> {hotel.cityName}, {hotel.countryName}
      </div>
      <div className="hotel-rating">
        <FontAwesomeIcon icon={faStar} /> {hotel.rating} Star
      </div>
    </div>

    {/* Right side - Change button */}
    {onChangeClick && (
      <button className="change-btn" onClick={onChangeClick}>
        <FontAwesomeIcon icon={faPen} /> Change
      </button>
    )}
  </div>
</div>

  );
};
const HotelSelectionCard = ({ hotel, onSelect, isSelected }) => {
  if (!hotel) return null;

  return (
    <div className="selection-card hotel-selection-card">
      <div className="selection-card-body">
        {/* Hotel Info */}
        <div className="hotel-info">
          <div className="hotel-name">
            <FontAwesomeIcon icon={faHotel} /> {hotel.hotelName}
          </div>
          <div className="hotel-location">
            <FontAwesomeIcon icon={faMapMarkerAlt} /> {hotel.cityName}, {hotel.countryName}
          </div>
          <div className="hotel-rating">
            <FontAwesomeIcon icon={faStar} /> {hotel.rating} Star
          </div>
        </div>

        {/* Select Button */}
         <div className="select-button-container">
        {isSelected ? (
            <button className="select-btn selected-btn" disabled>
                 Selected
            </button>
        ) : (
            <button className="select-btn" onClick={() => onSelect(hotel)}>
                Select
            </button>
        )}
      </div>
      </div>
    </div>
  );
};

const DayCard = ({ day, isOpen, onToggle, onChangeHotelClick }) => {
    const [activeTab, setActiveTab] = useState('activities');
    const hasHotel = day.hotel && day.hotel.length > 0;
    const hasTours = day.tours && day.tours.length > 0;
    const hasTransfer = !!day.transfer;

    return (
        <div className={`day-card ${isOpen ? 'is-open' : ''}`}>
            <div className="day-card-header" onClick={onToggle}>
                <h6>Day {day.dayNumber}: {day.title || day.area}</h6>
                <div className='d-flex align-items-center'>
                    <div className="date-display"><FontAwesomeIcon icon={faCalendarDay} /> {moment(day.date).format("ddd, MMM D")}</div>
                    <FontAwesomeIcon className="collapse-icon" icon={isOpen ? faChevronUp : faChevronDown} />
                </div>
            </div>
            {isOpen && (
                <div className="day-card-body">
                    <p className="day-summary">{day.summary}</p>
                    <div className="day-card-tabs">
                       {hasHotel && <button onClick={() => setActiveTab('hotel')} className={activeTab === 'hotel' ? 'active' : ''}><FontAwesomeIcon icon={faBed} /> Hotel</button>}
                        <button onClick={() => setActiveTab('activities')} className={activeTab === 'activities' ? 'active' : ''}><FontAwesomeIcon icon={faTasks} /> Activities</button>
                        {hasTransfer && <button onClick={() => setActiveTab('transfer')} className={activeTab === 'transfer' ? 'active' : ''}><FontAwesomeIcon icon={faRoute} /> Transfer</button>}
                    </div>
                    <div className="day-card-content-panel">
                      {activeTab === 'hotel' && hasHotel && <HotelDetailCard hotel={day.hotel[0]} onChangeClick={onChangeHotelClick} />}
                        {activeTab === 'activities' && <ul className="activities-list">{day.activities.map((act, i) => <li key={i}>{act}</li>)}</ul>}

                        {activeTab === 'transfer' && hasTransfer && <TransferDetailCard transfer={day.transfer} />}
                    </div>
                    {day.notes && (<p className="day-notes"><FontAwesomeIcon icon={faStickyNote} /> <strong>Note:</strong> {day.notes}</p>)}
                </div>
            )}
        </div>
    );
};

const AIPackageItinerary = ({ packageId, initialData, onContinueClick }) => {
    const { user } = useAuth();
    const navigate = useNavigate(); 
    const [btnLoading, setBtnLoading] = useState(false);
    const [dailyData, setDailyData] = useState([]);
    const [selectedOnwardFlight, setSelectedOnwardFlight] = useState(null);
    const [selectedReturnFlight, setSelectedReturnFlight] = useState(null);
    const [allOnwardFlights, setAllOnwardFlights] = useState([]);
    const [allReturnFlights, setAllReturnFlights] = useState([]);
    const [allHotels, setAllHotels] = useState([]);
    const [isFlightModalOpen, setFlightModalOpen] = useState(false);
    const [flightModalConfig, setFlightModalConfig] = useState({ type: null, options: [] });
    const [isHotelModalOpen, setHotelModalOpen] = useState(false);
    const [hotelModalConfig, setHotelModalConfig] = useState({ dayIndex: null });
    const [openDay, setOpenDay] = useState(1);

    const toggleDayCard = (dayNum) => {
        setOpenDay(openDay === dayNum ? null : dayNum);
    };

    // Inside the AIPackageItinerary component...

useEffect(() => {
    if (initialData && initialData.days && initialData.days.length > 0) {
        const dataCopy = JSON.parse(JSON.stringify(initialData.days));
        
        // --- START OF FIX ---
        // Get the entire flight object from the 'flights' array, not just the legs
        const onwardFlightObject = dataCopy[0]?.flights?.[0]; 
        const lastDay = dataCopy[dataCopy.length - 1];
        const returnFlightObject = lastDay?.flights?.[0];

        // The 'all' flights should be the full objects too
        setAllOnwardFlights(dataCopy[0]?.flights || []);
        setAllReturnFlights(lastDay?.flights || []);
        
        if (onwardFlightObject) setSelectedOnwardFlight(onwardFlightObject);
        if (returnFlightObject) setSelectedReturnFlight(returnFlightObject);
        // --- END OF FIX ---

        const uniqueHotels = [];
        const hotelIds = new Set();
        dataCopy.forEach(day => {
            if (day.hotel && day.hotel.length > 0 && !hotelIds.has(day.hotel[0].hotelId)) {
                uniqueHotels.push(day.hotel[0]);
                hotelIds.add(day.hotel[0].hotelId);
            }
        });
        setAllHotels(uniqueHotels);
        setDailyData(dataCopy);
    }
}, [initialData]);

    const handleOpenFlightModal = (type) => {
        setFlightModalConfig({
            type: type,
            options: type === 'onward' ? allOnwardFlights : allReturnFlights,
            title: type === 'onward' ? 'Select Departure Flight' : 'Select Return Flight'
        });
        setFlightModalOpen(true);
    };

    const handleSelectFlight = (flight) => {
        if (flightModalConfig.type === 'onward') {
            setSelectedOnwardFlight(flight);
        } else {
            setSelectedReturnFlight(flight);
        }
        setFlightModalOpen(false);
    };

    const handleOpenHotelModal = (dayIndex) => {
        setHotelModalConfig({ dayIndex });
        setHotelModalOpen(true);
    };

    const handleSelectHotel = (newHotel) => {
        const { dayIndex } = hotelModalConfig;
        if (dayIndex === null) return;
        const updatedDailyData = [...dailyData];
        const oldHotelId = updatedDailyData[dayIndex].hotel[0].hotelId;
        for (let i = 0; i < updatedDailyData.length; i++) {
            if (updatedDailyData[i].hotel?.[0]?.hotelId === oldHotelId) {
                updatedDailyData[i].hotel[0] = { ...newHotel };
            }
        }
        setDailyData(updatedDailyData);
        setHotelModalOpen(false);
    };
const handleSaveAllSuggestions = async () => {
        if (!initialData || !initialData.days) {
            alert("No itinerary data to save.");
            return;
        }

        setBtnLoading(true);

        try {
            // STEP 1: Create the Main Package
            const packagePayload = {
                packageName: initialData.packageName.substring(0, 49), // Truncate to 49 chars
                duration: initialData.duration,
                startedDate: initialData.startedDate,
                destinationsCover: initialData.destinationsCover,
                noOfPersons: initialData.noOfPersons,
                includes: initialData.includes,
                description: initialData.description, 
                price: initialData.price,
                imageName: initialData.imageName,
                type: "Holiday",
                companyId: user?.cid,
                userId: user?.id,
                destinationId: "cf4cce04-b9fc-42a6-b545-2fac0a52e094",
                travelTypeId: "253f65cb-9577-4cdd-9dba-7284732fbf2d",
            };

            const packageFormData = new FormData();
            for (const key in packagePayload) {
                packageFormData.append(key, packagePayload[key] ?? "");
            }

            const packageResponse = await apiService.create(config.CREATEPACKAGE, packageFormData);
            const newPackageId = packageResponse.packageId;

            if (!newPackageId) {
                throw new Error("Failed to create package. No packageId returned.");
            }

            // STEP 2: Create all related items using the newPackageId
            const promises = [];

            initialData.days.forEach(day => {
                // Save Itinerary for the day
                promises.push(apiService.create(config.CREATEPACKAGEITINERARY, {
                    packageId: newPackageId,
                    title: day.title,
                    date: day.date,
                    location: day.area,
                    description: day.summary,
                    meals: day.meals || "Not Specified",
                    note: day.notes,
                    userId: user?.id,
                }));

                // Save Hotel for the day
                if (day.hotel && day.hotel.length > 0) {
                    const hotel = day.hotel[0];
                    promises.push(apiService.create(config.CREATEPACKAGEHOTEL, {
                        packageId: newPackageId,
                        checkInDate: day.date,
                        checkOutDate: moment(day.date).add(day.nights || 1, 'days').format("YYYY-MM-DD"),
                        city: hotel.cityName,
                        nights: day.nights || 1,
                        hotelName: hotel.hotelName,
                        roomType: day.roomType || "Standard",
                        numberOfRooms: day.numberOfRooms || 1,
                        userId: user?.id,
                    }));
                }
                
                // Save Sightseeing/Tours for the day
                if (day.tours && day.tours.length > 0) {
                    day.tours.forEach(tour => {
                        promises.push(apiService.create(config.CREATEPACKAGESIGHT, {
                            packageId: newPackageId,
                            date: day.date,
                            destination: day.area,
                            sightseeingName: tour.tourName || tour.description.substring(0, 50), // Fallback name
                            description: tour.description,
                            userId: user?.id,
                        }));
                    });
                }
                
                // Save Transfer for the day
                if (day.transfer && day.transfer.from) {
                     promises.push(apiService.create(config.CREATEPACKAGETRANSFER, {
                        packageId: newPackageId,
                        date: day.date,
                        pickupTime: day.transfer.pickupTime || day.pickupTime || "N/A",
                        fromLocation: day.transfer.from,
                        toLocation: day.transfer.to,
                        vehicleDetails: day.transfer.vehicleType,
                        userId: user?.id,
                    }));
                }
            });

            // Save Flights (using the selected ones from state)
            if (selectedOnwardFlight) {
                const leg = selectedOnwardFlight.dcFlightLegs[0];
                const day = initialData.days[0];
                promises.push(apiService.create(config.CREATEPACKAGEFLIGHT, {
                    packageId: newPackageId,
                    airline: leg.supplierName,
                    flightNumber: day.flightNumber || "AI-Suggest",
                    departureCity: selectedOnwardFlight.locationFrom,
                    arrivalCity: selectedOnwardFlight.locationTo,
                    departureDateTime: day.departureDateTime || `${day.date}T09:00:00Z`,
                    arrivalDateTime: day.arrivalDateTime || `${day.date}T13:00:00Z`,
                    cabinClass: day.cabinClass || "Economy",
                    userId: user?.id,
                }));
            }
            
            if (selectedReturnFlight) {
                 const leg = selectedReturnFlight.dcFlightLegs[0];
                 const day = initialData.days[initialData.days.length - 1];
                 promises.push(apiService.create(config.CREATEPACKAGEFLIGHT, {
                    packageId: newPackageId,
                    airline: leg.supplierName,
                    flightNumber: day.flightNumber || "AI-Suggest",
                    departureCity: selectedReturnFlight.locationFrom,
                    arrivalCity: selectedReturnFlight.locationTo,
                    departureDateTime: day.departureDateTime || `${day.date}T18:00:00Z`,
                    arrivalDateTime: day.arrivalDateTime || `${day.date}T22:00:00Z`,
                    cabinClass: day.cabinClass || "Economy",
                    userId: user?.id,
                 }));
            }
            inclusions.forEach(item => {
                promises.push(apiService.create(config.CREATEPACKAGEINCLUDE, {
                    packageId: newPackageId,
                    description: item,
                    userId: user?.id,
                }));
            });
            
            // Save Exclusions from your constant array
            exclusions.forEach(item => {
                promises.push(apiService.create(config.CREATEPACKAGEEXCLUDE, {
                    packageId: newPackageId,
                    description: item,
                    userId: user?.id,
                }));
            });

            // Save Terms from your constant array of objects
            terms.forEach(term => {
                promises.push(apiService.create(config.CREATEPACKAGETERMS, {
                    packageId: newPackageId,
                    title: term.title,
                    description: term.description,
                    userId: user?.id,
                }));
            });

            await Promise.all(promises);

            alert("Package and all details saved successfully!");
            navigate(`/package/${newPackageId}`);

        } catch (error) {
            console.error("Error saving the full package:", error);
            alert(`An error occurred while saving: ${error.message || 'Please check the console for details.'}`);
        } finally {
            setBtnLoading(false);
        }
    };
    const handleContinue = () => {
        const finalData = {
            destination: initialData.destination,
            currency: initialData.currency,
            days: dailyData,
            selectedOnwardFlight: selectedOnwardFlight,
            selectedReturnFlight: selectedReturnFlight
        };
        onContinueClick(2, finalData); // Pass step and the final data up
    };
    
    const arrivalTransfer = dailyData?.[0]?.transfer;
    const departureTransfer = dailyData?.[dailyData.length - 1]?.transfer;
    
    // Data for the static section
   const inclusions = [ 
        "Return economy class airfare", 
        "Accommodation in specified hotels", 
        "Daily breakfast at the hotel", 
        "All transfers and sightseeing by private vehicle", 
        "All applicable taxes" 
    ];
    
    const exclusions = [ 
        "Visa fees", 
        "Travel insurance", 
        "Lunch and dinner unless specified", 
        "Any personal expenses", 
        "Tips and porter charges" 
    ];
    
    // For terms, let's create an array of objects to match your database structure
    const terms = [ 
        { title: "Pricing", description: "Package rates are subject to change without prior notice." },
        { title: "Availability", description: "Booking confirmation is subject to availability of flights and hotels." },
        { title: "Cancellation", description: "Cancellation charges apply as per the policy." }
    ];
     if (dailyData.length === 0) {
        return (
            <div className="text-center p-5">
                <h5>No AI Itinerary to display.</h5>
                <p>Please generate one from the 'AI Itinerary Builder' tab.</p>
            </div>
        );
    }
    return (
        <>
            <div>
                {dailyData.length > 0 ? (
                    <div className="ai-suggestions-container mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                             <h5 className="mb-0">AI Suggested Itinerary</h5>
                             <div className="d-flex gap-2">
                                <button className="btn btn-success" onClick={handleSaveAllSuggestions} disabled={btnLoading}>
                                     <FontAwesomeIcon icon={faSave} /> {btnLoading ? 'Saving...' : 'Save All Suggestions'}
                                </button>
                                <button className="btn btn-primary" onClick={handleContinue}>
                                     Continue to Share Template
                                </button>
                             </div>
                         </div>

                        <div className="itinerary-wrapper">
                            <div className="journey-segment departure-segment">
                                <div className="segment-marker"><FontAwesomeIcon icon={faPlaneDeparture} /></div>
                                <div className="segment-content">
                                    <h3 className="segment-title">Departure Journey</h3>
                                   <p className="segment-subtitle">
                                        From {selectedOnwardFlight?.locationFrom} to {selectedOnwardFlight?.locationTo}
                                    </p>
                                    
                                    <FlightDetailCard flight={selectedOnwardFlight} onChangeClick={() => handleOpenFlightModal('onward')} />
                                    <h5 className="sub-heading">Arrival Transfer</h5>
                                    <TransferDetailCard transfer={dailyData[0]?.transfer} />
                                </div>
                            </div>
                            <div className="journey-segment daily-itinerary-segment">
                                <div className="segment-marker"><FontAwesomeIcon icon={faCalendarDay} /></div>
                                <div className="segment-content">
                                    <h3 className="segment-title">Daily Itinerary</h3>
                                    {dailyData.map((day, index) => (
                                        <DayCard 
                                            key={day.dayNumber} 
                                            day={day} 
                                            isOpen={openDay === day.dayNumber}
                                            onToggle={() => toggleDayCard(day.dayNumber)}
                                            onChangeHotelClick={() => handleOpenHotelModal(index)} 
                                        />
                                    ))}
                                </div>
                            </div>
                            {(departureTransfer || selectedReturnFlight) && (
                                <div className="journey-segment return-segment">
                                    <div className="segment-marker"><FontAwesomeIcon icon={faPlaneArrival} /></div>
                                    <div className="segment-content">
                                        <h3 className="segment-title">Return Journey</h3>
                                         {dailyData[dailyData.length - 1]?.transfer && <> <h5 className="sub-heading">Departure Transfer</h5> <TransferDetailCard transfer={dailyData[dailyData.length - 1]?.transfer} /> </>}
                                        
                                        {selectedReturnFlight && <FlightDetailCard flight={selectedReturnFlight} onChangeClick={() => handleOpenFlightModal('return')} />}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                ) : (
                    <div className="text-center p-5">
                        <h5>No AI Itinerary to display.</h5>
                        <p>Please generate one from the 'AI Itinerary Builder' tab.</p>
                    </div>
                )}

                <SelectionModal isOpen={isFlightModalOpen} onClose={() => setFlightModalOpen(false)} title={flightModalConfig.title}>
    <div className="selection-list">
        {flightModalConfig.options.map(flight => (
            <FlightSelectionCard 
                key={flight.productKey || flight.dcFlightLegs[0].productKey} // Use a reliable key
                flight={flight} 
                onSelect={handleSelectFlight}
                // --- THIS IS THE NEW PROP ---
                isSelected={flight === (flightModalConfig.type === 'onward' ? selectedOnwardFlight : selectedReturnFlight)}
            />
        ))}
    </div>
</SelectionModal>

                
                <SelectionModal isOpen={isHotelModalOpen} onClose={() => setHotelModalOpen(false)} title="Select a Hotel">
    <div className="selection-list">
        {allHotels.map(hotel => (
            <HotelSelectionCard 
                key={hotel.hotelId} 
                hotel={hotel} 
                onSelect={handleSelectHotel} 
                // --- THIS IS THE NEW PROP ---
                isSelected={hotel.hotelId === dailyData[hotelModalConfig.dayIndex]?.hotel[0]?.hotelId}
            />
        ))}
    </div>
</SelectionModal>
            </div>

            {/* --- Inclusions, Exclusions & Terms Section --- */}
            <div className="additional-details-container" style={{ marginTop: '2rem' }}>
                <div className="details-section-card">
                  <h3>Inclusions</h3>
                  <ul className="info-list include-list">
                    {inclusions.map((item, index) => (
                      <li key={index}>
                        <FontAwesomeIcon icon={faCheck} className="list-icon" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="details-section-card">
                  <h3>Exclusions</h3>
                  <ul className="info-list exclude-list">
                    {exclusions.map((item, index) => (
                      <li key={index}>
                        <FontAwesomeIcon icon={faTimes} className="list-icon" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="details-section-card">
  <h3>Terms & Conditions</h3>
  <ol className="info-list terms-list">
    {terms.map((item, index) => (
      // --- START OF FIX ---
      <li key={index}>
        <strong>{item.title}:</strong> {item.description}
      </li>
      // --- END OF FIX ---
    ))}
  </ol>
</div>
            </div>
        </>
    );
};

export default AIPackageItinerary;
